import { InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';
import type { CropType } from '/@/types/crop-monitoring';
import { capitalizeFirstLetter } from '/@/utils';

interface EnvironmentInputProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    units: UnitConverter;
    error?: string;
    minEnvironment: number;
    maxEnvironment: number;
    crop: CropType | 'other';
    valueType?: string;
}

const EnvironmentInput = ({
    value,
    onChange,
    units,
    error,
    minEnvironment,
    maxEnvironment,
    crop,
    valueType = _('ambiente'),
}: EnvironmentInputProps) => {
    const handleChange = (inputValue: string) => {
        const numericValue = units.fromYield(inputValue, crop);
        onChange(numericValue);
    };

    const validationError = useMemo(() => {
        if (!value) return null;
        if (value < minEnvironment) {
            return _('El {{valueType}} debe ser mayor a {{minEnvironment}}', {
                valueType,
                minEnvironment: units.toYield(minEnvironment, crop, 2).localeValue,
            });
        }
        if (value > maxEnvironment) {
            return _('El {{valueType}} debe ser menor a {{maxEnvironment}}', {
                valueType,
                maxEnvironment: units.toYield(maxEnvironment, crop, 2).localeValue,
            });
        }
        return null;
    }, [value, units, minEnvironment, maxEnvironment, valueType, crop]);

    return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {_('{{valueType}} del lote', { valueType: capitalizeFirstLetter(valueType) })}
            </Typography>
            <TextField
                placeholder={_('Indicá el {{valueType}} productivo del lote', { valueType })}
                value={units.toYield(value, crop, 2).value || ''}
                sx={{
                    background: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                        top: '-10px',
                    },
                }}
                onChange={(e) => handleChange(e.target.value)}
                fullWidth
                type='number'
                error={!!error || !!validationError}
                helperText={
                    error ||
                    validationError ||
                    _(
                        `${_('Rango')} ${units.toYield(minEnvironment, crop, 2).localeValue} - ${units.toYield(maxEnvironment, crop, 2).localeValue} ${units.getUnit('yield', crop)}`
                    )
                }
                inputProps={{ 'data-testid': 'field-monitoring-creation-dialog-environment-input' }}
                InputProps={{
                    endAdornment: <InputAdornment position='end'>{units.getUnit('yield', crop)}</InputAdornment>,
                }}
            />
        </Stack>
    );
};

export default EnvironmentInput;
